import { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Clock, Mail, Send } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import SEO from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    course: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const courses = [
    'Computer Fundamentals & Typing',
    'MS Office Suite (Word, Excel, PowerPoint)',
    'Tally with GST',
    'C & C++ Programming',
    'Java Programming',
    'Photoshop',
    'CorelDraw',
    'PageMaker',
    'AI Tools Training',
    'Kannada Software (Nudi)',
    'Other / Not Sure',
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/leads`, formData);
      toast.success('Thank you! We\'ll contact you soon.');
      setFormData({ name: '', phone: '', course: '', message: '' });
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="pt-16 md:pt-20">
      <SEO
        title="Contact Us — Computer Institute in Kalaburagi (Gulbarga)"
        description="Contact Genius Academy — Computer Training Institute in Kalaburagi (Gulbarga). Call 9164998997 / 9980147616. Address: 2nd Floor, Darshan Orchid Complex, MSK Mill Road, Vidya Nagar, Kalaburagi. Book a free demo class today."
        path="/contact"
        keywords="Contact Genius Academy, computer institute address Kalaburagi, computer classes phone number Gulbarga, book demo class"
        breadcrumbs={[{ name: 'Home', path: '/' }, { name: 'Contact', path: '/contact' }]}
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-50 to-cyan-50 py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-6xl font-bold tracking-tight text-slate-900 mb-6" data-testid="contact-page-title">
              Get In <span className="text-cyan-600">Touch</span>
            </h1>
            <p className="text-base md:text-lg text-slate-600 max-w-3xl mx-auto leading-relaxed">
              Have questions? Want to book a free demo? We're here to help. Reach out today!
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold text-slate-900 mb-8">Contact Information</h2>

              <div className="space-y-6 mb-8">
                <div className="flex items-start gap-4">
                  <div className="bg-cyan-100 w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-cyan-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900 mb-1">Phone</h3>
                    <a href="tel:9164998997" className="text-slate-600 hover:text-cyan-600 transition-colors block">
                      9164998997
                    </a>
                    <a href="tel:9980147616" className="text-slate-600 hover:text-cyan-600 transition-colors block">
                      9980147616
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-cyan-100 w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-cyan-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900 mb-1">Address</h3>
                    <p className="text-slate-600">
                      2nd floor, Darshan Orchid complex,<br />
                      MSK Mill Rd, opposite Sangameshwar Hospital,<br />
                      Vidya Nagar, Kalaburagi, Karnataka 585103
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-cyan-100 w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-cyan-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900 mb-1">Timing</h3>
                    <p className="text-slate-600">
                      Monday - Saturday: 8:00 AM - 8:00 PM<br />
                      Sunday: Closed
                    </p>
                  </div>
                </div>
              </div>

              {/* Google Map */}
              <div className="rounded-2xl overflow-hidden shadow-lg" data-testid="genius-academy-map-wrapper">
                <iframe
                  src="https://maps.google.com/maps?q=17.3251499,76.8197221(Genius+Academy)&hl=en&z=19&output=embed"
                  width="100%"
                  height="256"
                  style={{ border: 0, display: 'block' }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Genius Academy - Google Maps location"
                  data-testid="genius-academy-map-iframe"
                ></iframe>
                <div className="flex flex-col sm:flex-row gap-2 p-3 bg-white border-t border-slate-100">
                  <a
                    href="https://maps.app.goo.gl/cXXM4Lyg81THD5xf7"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-semibold px-4 py-2.5 transition-colors"
                    data-testid="open-in-google-maps"
                  >
                    <MapPin className="w-4 h-4" />
                    Open in Google Maps
                  </a>
                  <a
                    href="https://www.google.com/maps/dir/?api=1&destination=17.3251499,76.8197221&destination_place_id=ChIJ_97fWUa5yDsRj2ywV7Cb4i0"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg bg-white border border-slate-200 hover:border-cyan-400 text-slate-800 text-sm font-semibold px-4 py-2.5 transition-colors"
                    data-testid="get-directions"
                  >
                    Get Directions
                  </a>
                </div>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="bg-white rounded-2xl border border-slate-100 shadow-lg p-8">
                <h2 className="text-3xl font-bold text-slate-900 mb-6">Book Your Free Demo</h2>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-2">
                      Your Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="bg-white border border-slate-200 rounded-lg px-4 py-3 focus:ring-2 focus:ring-cyan-500 focus:border-transparent outline-none transition-all w-full"
                      placeholder="Enter your full name"
                      data-testid="contact-form-name"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-slate-700 mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={handleChange}
                      className="bg-white border border-slate-200 rounded-lg px-4 py-3 focus:ring-2 focus:ring-cyan-500 focus:border-transparent outline-none transition-all w-full"
                      placeholder="10-digit mobile number"
                      pattern="[0-9]{10}"
                      data-testid="contact-form-phone"
                    />
                  </div>

                  <div>
                    <label htmlFor="course" className="block text-sm font-medium text-slate-700 mb-2">
                      Course Interested In *
                    </label>
                    <select
                      id="course"
                      name="course"
                      required
                      value={formData.course}
                      onChange={handleChange}
                      className="bg-white border border-slate-200 rounded-lg px-4 py-3 focus:ring-2 focus:ring-cyan-500 focus:border-transparent outline-none transition-all w-full"
                      data-testid="contact-form-course"
                    >
                      <option value="">Select a course</option>
                      {courses.map((course, index) => (
                        <option key={index} value={course}>
                          {course}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-slate-700 mb-2">
                      Message (Optional)
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows="4"
                      value={formData.message}
                      onChange={handleChange}
                      className="bg-white border border-slate-200 rounded-lg px-4 py-3 focus:ring-2 focus:ring-cyan-500 focus:border-transparent outline-none transition-all w-full"
                      placeholder="Any specific questions or requirements?"
                      data-testid="contact-form-message"
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-orange-500 hover:bg-orange-600 disabled:bg-slate-300 text-white rounded-full px-8 py-4 font-bold shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1 disabled:transform-none flex items-center justify-center gap-2"
                    data-testid="contact-form-submit"
                  >
                    {isSubmitting ? 'Submitting...' : (
                      <>
                        <Send className="w-5 h-5" />
                        Submit Enquiry
                      </>
                    )}
                  </button>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;